(() => {
  'use strict';
  if (customElements.get('zelloa-promo-banner')) return;

  const formatters = new Map();
  let visitorZoneResolved = false;
  let visitorZone = null;
  function getFormatter(timeZone) {
    if (!formatters.has(timeZone)) {
      try {
        formatters.set(timeZone, new Intl.DateTimeFormat('en-CA', {
          timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
        }));
      } catch (_) { formatters.set(timeZone, null); }
    }
    return formatters.get(timeZone);
  }
  function getVisitorZone() {
    if (!visitorZoneResolved) {
      visitorZoneResolved = true;
      try {
        const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (zone && getFormatter(zone)) visitorZone = zone;
      } catch (_) { /* Use the configured fallback without any geolocation request. */ }
    }
    return visitorZone;
  }

  class ZelloaPromoBanner extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.timer = this.querySelector('[data-zpb-timer]');
      this.originalTitle = this.querySelector('[data-zpb-title]')?.textContent;
      this.originalSubtitle = this.querySelector('[data-zpb-subtitle]')?.textContent;
      this.units = Object.fromEntries(Array.from(this.querySelectorAll('[data-zpb-unit]'), node => [node.dataset.zpbUnit, node]));
      const refresh = () => {
        clearTimeout(this.refreshTimeout);
        this.refreshTimeout = setTimeout(() => this.configure(), 0);
      };
      document.addEventListener('shopify:section:load', refresh, { signal: this.controller.signal });
      document.addEventListener('shopify:section:unload', refresh, { signal: this.controller.signal });
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start(), { signal: this.controller.signal });
      // Pause the 1 s tick while the banner is scrolled out of view; start() repaints immediately on return.
      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver(entries => {
          this.offscreen = !entries[entries.length - 1].isIntersecting;
          if (this.offscreen) this.stop();
          else if (!this.interval) this.start();
        });
        this.viewObserver.observe(this);
      }
      this.configure();
    }

    configure() {
      this.stop();
      const source = this.dataset.inheritCountdown === 'true'
        ? document.querySelector('zelloa-promo-banner.zpb[data-countdown-mode]') : null;
      this.mode = this.dataset.countdownMode || source?.dataset.countdownMode || 'daily';
      const configuredZone = this.dataset.timeZone || source?.dataset.timeZone || 'America/New_York';
      this.timezoneMode = this.dataset.timezoneMode || source?.dataset.timezoneMode
        || (this.dataset.inheritCountdown === 'true' && !source ? 'visitor' : 'manual');
      const detectedZone = this.timezoneMode === 'visitor' ? getVisitorZone() : null;
      this.timeZone = detectedZone || configuredZone;
      this.timezoneSource = detectedZone ? 'visitor' : this.timezoneMode === 'visitor' ? 'fallback' : 'manual';
      const raw = this.dataset.deadline || source?.dataset.deadline || '';
      this.hidden = false;
      if (this.mode === 'daily') {
        this.deadline = this.nextMidnight(Date.now(), this.timeZone);
      } else {
        this.deadline = this.fixedDeadline(raw);
      }
      if (!Number.isFinite(this.deadline)) {
        this.dataset.timerState = 'invalid';
        if (this.timer) this.timer.hidden = true;
        if (this.dataset.inheritCountdown === 'true' && this.dataset.designMode !== 'true') this.hidden = true;
        return;
      }
      const title = this.querySelector('[data-zpb-title]');
      const subtitle = this.querySelector('[data-zpb-subtitle]');
      if (title && this.originalTitle != null) title.textContent = this.originalTitle;
      if (subtitle && this.originalSubtitle != null) subtitle.textContent = this.originalSubtitle;
      this.start();
    }

    fixedDeadline(raw) {
      const parts = String(raw || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:Z|[+-]\d{2}:\d{2})$/);
      if (!parts) return NaN;
      const values = parts.slice(1).map(value => Number(value || 0));
      const [year, month, day, hour, minute, second] = values;
      const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
      const valid = month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth
        && hour < 24 && minute < 60 && second < 60;
      return valid ? Date.parse(raw) : NaN;
    }

    zonedParts(epoch, timeZone) {
      try {
        const formatter = getFormatter(timeZone);
        if (!formatter) return null;
        return Object.fromEntries(formatter.formatToParts(new Date(epoch))
          .filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
      } catch (_) {
        return null;
      }
    }

    zonedEpoch(parts, timeZone) {
      const desired = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour || 0, parts.minute || 0, parts.second || 0);
      let guess = desired;
      for (let pass = 0; pass < 5; pass += 1) {
        const observed = this.zonedParts(guess, timeZone);
        if (!observed) return NaN;
        const observedEpoch = Date.UTC(observed.year, observed.month - 1, observed.day, observed.hour, observed.minute, observed.second);
        const correction = desired - observedEpoch;
        guess += correction;
        if (!correction) break;
      }
      return guess;
    }

    nextMidnight(now, timeZone) {
      const local = this.zonedParts(now, timeZone);
      if (!local) return NaN;
      const tomorrow = new Date(Date.UTC(local.year, local.month - 1, local.day + 1));
      return this.zonedEpoch({
        year: tomorrow.getUTCFullYear(), month: tomorrow.getUTCMonth() + 1,
        day: tomorrow.getUTCDate(), hour: 0, minute: 0, second: 0
      }, timeZone);
    }

    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.refreshTimeout);
      this.viewObserver?.disconnect();
      this.viewObserver = null;
      this.offscreen = false;
    }

    stop() {
      clearInterval(this.interval);
      this.interval = null;
    }

    start() {
      this.stop();
      if (Number.isFinite(this.deadline) && this.tick() && !document.hidden && !this.offscreen) this.interval = setInterval(() => this.tick(), 1000);
    }

    tick() {
      const now = Date.now();
      if (this.mode === 'daily' && now >= this.deadline) this.deadline = this.nextMidnight(now, this.timeZone);
      const remaining = Math.max(0, Math.ceil((this.deadline - now) / 1000));
      if (!remaining) {
        if (this.mode === 'daily') {
          this.dataset.timerState = 'invalid';
          if (this.timer) this.timer.hidden = true;
        } else {
          this.expire();
        }
        return false;
      }
      // Write only what changed: identical writes still queue MutationRecords that every
      // document-level observer (Clarity, app injectors) must process in this timer task.
      if (this.dataset.timerState !== 'running') this.dataset.timerState = 'running';
      const values = { days: Math.floor(remaining / 86400), hours: Math.floor(remaining % 86400 / 3600), minutes: Math.floor(remaining % 3600 / 60), seconds: remaining % 60 };
      this.querySelectorAll('[data-zpb-days]').forEach(node => { const hide = values.days === 0; if (node.hidden !== hide) node.hidden = hide; });
      for (const [key, number] of Object.entries(values)) {
        const node = this.units[key];
        const value = String(number).padStart(2, '0');
        if (node && node.textContent !== value) {
          // Updating the Text node in place emits characterData only, not a childList record.
          const text = node.firstChild;
          if (text && text.nodeType === 3 && !text.nextSibling) text.data = value;
          else node.textContent = value;
        }
      }
      if (this.timer) this.timer.hidden = false;
      return true;
    }

    expire() {
      this.stop();
      this.dataset.timerState = 'expired';
      if (this.timer) this.timer.hidden = true;
      if (this.dataset.expiredBehavior === 'hide' && this.dataset.designMode !== 'true') {
        this.hidden = true;
        return;
      }
      const title = this.querySelector('[data-zpb-title]');
      const subtitle = this.querySelector('[data-zpb-subtitle]');
      if (title) title.textContent = this.dataset.expiredTitle || '';
      if (subtitle) subtitle.textContent = this.dataset.expiredSubtitle || '';
    }
  }
  customElements.define('zelloa-promo-banner', ZelloaPromoBanner);
})();
